/* --- BL CART GIFTS CONTROLLER: START ------------------------------------------ */
/* Automatic shop gifts. The BL cart integration waits for sync() before returning cart mutations to the theme, so
   the drawer never opens showing a cart the gifts have not caught up with.

   Round trips are the whole design problem here — every one of them is a Shopify request the shopper waits on — so
   three things keep the count down:

     - The page is RENDERED with the cart (snippets/bl_cart-gifts.liquid emits it beside the rules), so a page load
       whose gift lines are already correct — nearly all of them — costs no requests at all.
     - Every gift line that has to move goes in ONE cart/update.js, and every gift that has to be added in ONE
       cart/add.js, instead of a request per line.
     - Those mutations carry `sections`, so the drawer's new markup comes back WITH them rather than costing a
       separate render afterwards.
*/
(() => {
  const config = document.getElementById('bl-cart-gift-rules');
  if (!config) return;

  const { rules, cart: renderedCart } = JSON.parse(config.textContent);
  // Our own transport, captured before the integration below replaces window.fetch: gift requests must not recurse
  // back into reconciliation.
  const cartFetch = window.fetch.bind(window);
  const GIFT_PROPERTY = '_bl_gift_mechanism';
  // Nothing may hold the cart — or the checkout button — open forever on a connection that never answers.
  const REQUEST_TIMEOUT = 10000;
  // Only a pass that CORRECTS existing lines can change what the next pass wants; see reconcile().
  const MAX_PASSES = 3;
  const RELOAD_GUARD_KEY = 'bl-cart-gifts-reloads';
  const MAX_RELOADS = 2;

  // `gift` is the rendered cart's spelling, `properties` the Ajax API's. Both describe the same line.
  const isGift = (item) => item.gift === true || item.properties?.[GIFT_PROPERTY] === '1';
  const cartState = (cart) => JSON.stringify([cart.total_price, cart.items.map((item) => [item.key, item.quantity])]);

  let queue = Promise.resolve();
  let queued = 0;
  let checkoutPending = false;

  const request = async (path, body) => {
    const signal = AbortSignal.timeout?.(REQUEST_TIMEOUT);
    const response = await cartFetch(`${Shopify.routes.root}${path}`, body ? {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
      signal
    } : { cache: 'no-store', signal });
    const result = await response.json();
    if (!response.ok) throw Object.assign(new Error(result.description || 'Unable to update cart gifts.'), { status: response.status });
    return result;
  };

  // The theme's own way of asking which sections a cart mutation should bring back (theme.js:2083). Answering it
  // ourselves lets the gift mutation carry them, which is one render instead of a second request.
  const bundledSections = () => {
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
    return [...new Set(sections)];
  };

  // Sections render in the context of the URL they are asked for. Without this they would render against the shop
  // root, which is the wrong page for anything outside the header and footer groups.
  const withSections = (body, sections) => (sections.length
    ? { ...body, sections, sections_url: `${window.location.pathname}${window.location.search}` }
    : body);

  const desiredGifts = (cart) => {
    const desired = new Map();
    if (!cart.items.some((item) => !isGift(item))) return desired;

    // Gifts never contribute towards their own qualification. Shopify supplies the discounted cart total.
    const spend = Math.max(0, cart.total_price - cart.items.filter(isGift).reduce((sum, item) => sum + item.final_line_price, 0));
    const currencyRate = Number(Shopify.currency?.rate) || 1;
    for (const rule of rules) {
      /* min <= spend <= max, both ends inclusive, and each end optional. A blank minimum arrives as 0 and a blank
         maximum as null, so the three shapes a merchant can write all fall out of these two guards:
           minimum only   spend >= min
           both           spend >= min and spend <= max
           maximum only   spend <= max
         Inclusive at both ends means tiers sharing a boundary overlap on it: with 0-100 and 100-200, a cart of
         exactly 100 wins both. Write bands that do not touch if that is not wanted. */
      if (spend < Math.round(rule.min * currencyRate)) continue;
      if (rule.max !== null && spend > Math.round(rule.max * currencyRate)) continue;
      for (const gift of rule.gifts) {
        if (!gift.available || !Number.isInteger(gift.quantity) || gift.quantity <= 0) continue;
        const id = String(gift.id);
        desired.set(id, (desired.get(id) || 0) + gift.quantity);
      }
    }
    return desired;
  };

  /* What this cart's gift lines would have to do to match the rules, as two batches rather than a list of steps:
     `updates` is every gift line key whose quantity is wrong (0 meaning remove), `additions` every gift not on the
     cart at all. Both are shaped for a single request. */
  const planChanges = (cart, failed) => {
    const remaining = desiredGifts(cart);
    const updates = {};

    for (const item of cart.items) {
      if (!isGift(item)) continue;
      // A gift the rules no longer want is not in `remaining` at all, so it keeps nothing and is removed.
      const keep = Math.min(item.quantity, remaining.get(String(item.variant_id)) || 0);
      remaining.set(String(item.variant_id), (remaining.get(String(item.variant_id)) || 0) - keep);
      if (keep !== item.quantity) updates[item.key] = keep;
    }

    const additions = [];
    for (const [id, quantity] of remaining) {
      if (quantity > 0 && !failed.has(id)) {
        additions.push({ id: Number(id), quantity, properties: { [GIFT_PROPERTY]: '1' } });
      }
    }

    return { updates, additions };
  };

  const inSync = (cart) => {
    const { updates, additions } = planChanges(cart, new Set());
    return Object.keys(updates).length === 0 && additions.length === 0;
  };

  const reconcile = async (cart, sections) => {
    const failed = new Set();
    const originalState = cartState(cart);
    let rendered = null;
    let added = false;

    for (let pass = 0; pass < MAX_PASSES; pass += 1) {
      const { updates, additions } = planChanges(cart, failed);
      if (!Object.keys(updates).length && !additions.length) break;

      // Corrections and removals first, in one request: update.js takes every line key at once AND answers with the
      // whole cart, so a further pass can re-plan without paying for a refetch.
      if (Object.keys(updates).length) {
        cart = await request('cart/update.js', withSections({ updates }, sections));
        rendered = cart.sections ?? rendered;
      }

      if (additions.length) {
        try {
          const result = await request('cart/add.js', withSections({ items: additions }, sections));
          rendered = result.sections ?? rendered;
          added = true;
        } catch (error) {
          if (error.status !== 422) throw error;
          // Inventory can move after the page was rendered. Drop the whole batch rather than retry it, and leave the
          // next reconciliation to pick up anything that comes back into stock.
          additions.forEach(({ id }) => failed.add(String(id)));
          console.warn('[Cart gifts]', error.message);
        }
        // Adding a gift cannot change qualification — desiredGifts() measures spend with gift lines excluded — so
        // there is nothing left for another pass to find, and `cart` is deliberately left un-refetched.
        break;
      }
    }

    return { cart, rendered, stale: added, changed: added || cartState(cart) !== originalState };
  };

  /* `needsCart` is false for add-to-cart, where the theme fetches the cart itself straight afterwards
     (theme.js:2936) and takes only our sections. Saying so keeps a gift add down to one request. */
  const sync = (initialCart, { needsCart = true } = {}) => {
    const refetch = queued > 0 || !initialCart;
    queued += 1;
    document.documentElement.setAttribute('data-bl-cart-gifts-busy', '');

    const task = queue.then(async () => {
      const sections = bundledSections();
      let cart = initialCart;
      let rendered = null;
      let changed = false;
      let originalState;

      try {
        if (refetch) cart = await request('cart.js');
        originalState = cartState(cart);
        const outcome = await reconcile(cart, sections);
        ({ cart, rendered, changed } = outcome);
        // The only case that leaves the cart body behind: add.js answers with the added items, never the cart.
        if (outcome.stale && needsCart) cart = await request('cart.js');
      } catch (error) {
        console.warn('[Cart gifts]', error);
        // A gift error must not turn a successful merchandise add into a failed add-to-cart message.
        cart = await request('cart.js').catch(() => cart);
        changed = Boolean(cart && originalState && cartState(cart) !== originalState);
      }

      if (!cart) return { cart: initialCart, sections: initialCart?.sections, rendered: null, changed: false };

      // Fresh markup where we have it, the theme's own where we do not: never a hole where a section should be.
      const merged = { ...initialCart?.sections };
      for (const [id, html] of Object.entries(rendered || {})) {
        if (html !== null) merged[id] = html;
      }
      cart.sections = merged;
      return { cart, sections: merged, rendered, changed };
    });

    queue = task.catch(() => {}).finally(() => {
      queued -= 1;
      if (queued === 0) document.documentElement.removeAttribute('data-bl-cart-gifts-busy');
    });
    return task;
  };

  window.blCartGifts = { sync };

  const drawerSectionId = () => {
    const drawer = document.querySelector('cart-drawer');
    return drawer?.closest('.shopify-section')?.id.replace('shopify-section-', '') || null;
  };

  // A rule the cart can never satisfy — a gift that is out of stock every time, an app that removes the line again —
  // would otherwise reload the cart page forever.
  const reloadGuard = {
    count: () => {
      try { return Number(sessionStorage.getItem(RELOAD_GUARD_KEY)) || 0; } catch { return MAX_RELOADS; }
    },
    spend: () => {
      try { sessionStorage.setItem(RELOAD_GUARD_KEY, String(reloadGuard.count() + 1)); } catch { /* private mode */ }
    },
    clear: () => {
      try { sessionStorage.removeItem(RELOAD_GUARD_KEY); } catch { /* private mode */ }
    }
  };

  // Existing carts also need reconciliation when the shopper returns or the merchant changes the rules.
  const refresh = async () => {
    const result = await sync();
    if (!result.changed || !result.cart) return;

    if (window.themeVariables?.settings?.pageType === 'cart') {
      if (reloadGuard.count() >= MAX_RELOADS) {
        console.warn('[Cart gifts] Gift lines are not settling; leaving the cart page as rendered.');
        return;
      }
      reloadGuard.spend();
      window.location.reload();
      return;
    }

    const sectionId = drawerSectionId();
    if (sectionId && !result.sections?.[sectionId]) {
      // cart:change would hand the drawer an undefined section to parse (theme.js:1841). Re-render it from the
      // server instead, which also keeps the cart count and free shipping bar correct.
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      return;
    }

    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: { baseEvent: 'variant:add', cart: result.cart }
    }));
  };

  document.addEventListener('DOMContentLoaded', () => {
    // The page was rendered with the cart, so the answer is already here: when the gift lines are right — which is
    // every load that is not the one right after a rule change or an outside edit — this costs nothing.
    if (!renderedCart || inSync(renderedCart)) {
      reloadGuard.clear();
      return;
    }
    refresh();
  });
  // A back/forward restore serves the cart as it was, which the rendered snapshot can no longer vouch for.
  window.addEventListener('pageshow', (event) => { if (event.persisted) refresh(); });

  // Do not submit checkout with gifts still being reconciled, or let a quantity edit use a key being replaced.
  document.addEventListener('submit', async (event) => {
    if (!queued || event.submitter?.name !== 'checkout') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkoutPending) return;
    checkoutPending = true;
    const form = event.target;
    const submitter = event.submitter;
    // Bounded: a reconciliation that never settles must not leave the checkout button permanently dead.
    const deadline = Date.now() + REQUEST_TIMEOUT;
    while (queued && Date.now() < deadline) {
      await Promise.race([queue, new Promise((resolve) => { setTimeout(resolve, 250); })]);
    }
    checkoutPending = false;
    if (form.isConnected && submitter.isConnected) form.requestSubmit(submitter);
  }, true);

  document.addEventListener('change', (event) => {
    const field = event.target;
    if (!queued || !field?.hasAttribute?.('data-line-key')) return;
    event.stopImmediatePropagation();
    // Put the field back to the quantity the cart still holds, so the number left on screen is never a lie.
    if ('defaultValue' in field) field.value = field.defaultValue;
  }, true);

  document.addEventListener('click', (event) => {
    if (!queued || !event.target.closest?.('line-item-quantity')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);
})();
/* --- BL CART GIFTS CONTROLLER: END -------------------------------------------- */

/* --- BL CART GIFTS INTEGRATION: START ------------------------------------------ */
/* Keep theme.js unchanged: reconcile gifts at the Shopify cart request boundary. */
(() => {
  if (!window.blCartGifts) return;

  const cartFetch = window.fetch.bind(window);
  const cartRoot = new URL(Shopify.routes.root, window.location.origin);
  const cartPath = `${cartRoot.pathname}cart.js`;
  const endpoints = new Map(['add', 'change', 'update', 'clear'].map((action) => [`${cartRoot.pathname}cart/${action}.js`, action]));

  /* After an add the theme fetches cart.js itself (theme.js:2936), moments after we fetched it to price the gift
     rules. When nothing about the cart changed in between, the second fetch is asking a question we are still
     holding the answer to — so hand it over once, and only within a window too short for anything else to have
     written to the cart. Any cart POST, ours included, throws it away. */
  let cached = null;
  // Bumped by every cart write. A read that started before a later write must not be published as the current cart.
  let writes = 0;
  const CACHE_TTL = 1000;
  const putCache = (body) => { cached = { body, at: Date.now() }; };
  const takeCache = () => {
    const entry = cached;
    cached = null;
    return entry && Date.now() - entry.at < CACHE_TTL ? entry.body : null;
  };

  window.fetch = async (input, options) => {
    const url = new URL(input instanceof Request ? input.url : input, window.location.href);
    const method = (options?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
    const sameOrigin = url.origin === cartRoot.origin;

    if (sameOrigin && method === 'GET' && url.pathname === cartPath) {
      const body = takeCache();
      if (body) return new Response(body, { status: 200, headers: { 'Content-Type': 'application/json' } });
      return cartFetch(input, options);
    }

    const action = sameOrigin && method === 'POST' ? endpoints.get(url.pathname) : null;
    if (!action) return cartFetch(input, options);

    cached = null;
    const stamp = ++writes;
    const response = await cartFetch(input, options);
    if (!response.ok && !(action === 'add' && response.status === 422)) return response;

    try {
      const original = await response.clone().json();
      // add.js answers with the added items rather than the cart, and gift rules are priced off cart totals.
      const cartBody = action === 'add' ? await (await cartFetch(`${Shopify.routes.root}cart.js`, { cache: 'no-store' })).text() : null;
      const cart = action === 'add' ? JSON.parse(cartBody) : original;
      if (!Array.isArray(cart.items)) return response;
      cart.sections = original.sections;

      const { cart: updatedCart, sections, rendered, changed } = await window.blCartGifts.sync(cart, { needsCart: action !== 'add' });
      // Preserve add.js's purchased-item payload and any inventory error. Only its cart sections change.
      if (!response.ok || !updatedCart) return response;

      // Untouched by the gift rules, and nothing has written to the cart since we read it: the body we are holding
      // is still the live one, so let the theme's own fetch have it.
      if (action === 'add' && !changed && stamp === writes) putCache(cartBody);

      if (changed && !rendered) {
        // Gifts moved but Shopify sent back no markup for them. Let the theme finish with what it has, then
        // re-render the cart from the server rather than leave the gift invisible until the next page load.
        setTimeout(() => document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true })), 0);
      }

      const result = action === 'add' ? { ...original, sections } : updatedCart;
      const headers = new Headers(response.headers);
      headers.delete('content-length');
      headers.delete('content-encoding');
      return new Response(JSON.stringify(result), { status: response.status, statusText: response.statusText, headers });
    } catch (error) {
      console.warn('[Cart gifts] Cart integration could not reconcile gifts.', error);
      return response;
    }
  };

  // A gift discount can change a paid line's key. Keep the successful quantity edit from being animated as a removal.
  const requestedQuantities = new WeakMap();
  document.addEventListener('line-item:will-change', (event) => {
    requestedQuantities.set(event.target, event.detail.targetQuantity);
  }, true);
  document.addEventListener('line-item:change', (event) => {
    const quantity = requestedQuantities.get(event.target);
    if (quantity > 0 && event.detail.quantity === 0) event.detail.quantity = quantity;
    requestedQuantities.delete(event.target);
  }, true);
  document.addEventListener('line-item:error', (event) => requestedQuantities.delete(event.target), true);
})();
/* --- BL CART GIFTS INTEGRATION: END -------------------------------------------- */

/* --- BL CART MESSAGE CARD: START ---------------------------------------------- */
/* The gift message on the cart page, stored as a cart ATTRIBUTE so it arrives on the order under its own name —
   "Additional details" in the admin, order.attributes["<name>"] in Liquid — instead of competing with the order note.

   The field sits inside the cart form, so Checkout already submits it. This saves it on `change` as well, exactly as
   the theme does for the order note (theme.js cart-note.js), so a shopper who writes a message and then carries on
   browsing still has it when they come back. */
(() => {
  class BlMessageCard extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', this.#onChange);
    }

    #onChange = (event) => {
      const field = event.target;
      const name = field?.dataset?.attributeName;
      if (!name) return;

      fetch(`${Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // As the theme's order note does: the request has to survive the form submit that may follow it immediately.
        keepalive: true,
        body: JSON.stringify({ attributes: { [name]: field.value } })
      }).catch((error) => console.warn('[Message card] The message could not be saved.', error));
    };
  }

  if (!window.customElements.get('bl-message-card')) {
    window.customElements.define('bl-message-card', BlMessageCard);
  }
})();
/* --- BL CART MESSAGE CARD: END ------------------------------------------------ */

/* --- BL PRODUCT GALLERY: START ------------------------------------------------ */
(async () => {
  const { deepQuerySelector } = await import('theme');

  // product:rerender does not bubble. Capture it before the native gallery updates its media filters.
  document.addEventListener('product:rerender', (event) => {
    const formId = event.target.getAttribute('id');
    if (!formId || !event.detail?.htmlFragment) return;

    document.querySelectorAll('product-gallery.product-gallery--desktop-grid').forEach((gallery) => {
      if (gallery.getAttribute('form') !== formId) return;
      const markup = deepQuerySelector(event.detail.htmlFragment, `product-gallery[form="${CSS.escape(formId)}"]`);
      if (!markup) return;

      const featuredMediaId = markup.querySelector('[data-grid-featured]')?.getAttribute('data-media-id');
      gallery.querySelectorAll('media-carousel > .product-gallery__media').forEach((media) => {
        media.toggleAttribute('data-grid-featured', media.getAttribute('data-media-id') === featuredMediaId);
      });
    });
  }, true);
})();
/* --- BL PRODUCT GALLERY: END -------------------------------------------------- */

/* ---------------------------------------------------------------------------
   Reviews (BL) — scroll fades
   The fades on .bl-review__scroll paint over the review text, so each must only
   show when there is hidden content in that direction. Toggles
   bl-can-scroll-up / bl-can-scroll-down on the .bl-review__scroll wrapper.
--------------------------------------------------------------------------- */
(() => {
  const SCROLLER_SELECTOR = '.bl-review__text-inner';
  const WRAPPER_SELECTOR = '.bl-review__scroll';
  const UP_CLASS = 'bl-can-scroll-up';
  const DOWN_CLASS = 'bl-can-scroll-down';
  const THRESHOLD = 1; // absorbs sub-pixel rounding
  const tracked = new WeakSet();

  const update = (scroller) => {
    const wrapper = scroller.closest(WRAPPER_SELECTOR);

    if (!wrapper) return;

    const overflow = scroller.scrollHeight - scroller.clientHeight;
    const scrollable = overflow > THRESHOLD;

    wrapper.classList.toggle(UP_CLASS, scrollable && scroller.scrollTop > THRESHOLD);
    wrapper.classList.toggle(DOWN_CLASS, scrollable && scroller.scrollTop < overflow - THRESHOLD);
  };

  const observer = new ResizeObserver((entries) => {
    entries.forEach((entry) => update(entry.target));
  });

  const scan = (root) => {
    root.querySelectorAll(SCROLLER_SELECTOR).forEach((scroller) => {
      update(scroller);

      if (tracked.has(scroller)) return;

      tracked.add(scroller);
      observer.observe(scroller);
      scroller.addEventListener('scroll', () => update(scroller), { passive: true });
    });
  };

  scan(document);

  // Fonts and images change the box height after first paint.
  window.addEventListener('load', () => scan(document));

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => scan(document));
  }

  // Theme editor: sections are re-rendered in place.
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();

/* ---------------------------------------------------------------------------
   Reels (BL) — persistent click-to-play toggle
   The theme's click-to-play handler runs only once, so a reel paused by another
   reel in its group cannot otherwise be replayed or paused by clicking it.
   Delegating from document survives product-rerender variant updates and also
   covers reels added by the theme editor without a page reload.
--------------------------------------------------------------------------- */
(() => {
  const MEDIA_SELECTOR = '.bl-reels-block__media';
  const FULLSCREEN_SELECTOR = '.bl-reels-block__fullscreen';
  const CONTROL_STRIP_HEIGHT = 48; // px — native control bars are ~40-45px across browsers

  // Fullscreen the <video>, never its video-media wrapper: theme.css applies the
  // 3/5 object-fit crop only while the video is not :fullscreen, so targeting
  // the wrapper would preserve that crop across the full screen.
  const enterFullscreen = (video) => {
    if (!video) return;

    try {
      // Prefer the standard API, then legacy WebKit fullscreen, then iOS Safari's
      // video-only API. Ordinary elements have no Fullscreen API on iPhone, so
      // webkitEnterFullscreen is the branch that supports this block's primary device.
      if (video.requestFullscreen) {
        video.requestFullscreen().catch(() => {});
      } else if (video.webkitRequestFullscreen) {
        video.webkitRequestFullscreen();
      } else if (video.webkitEnterFullscreen) {
        video.webkitEnterFullscreen();
      }
    } catch (error) {
      // iOS throws if it is called before metadata has loaded; nothing to recover, stay inline.
    }
  };

  document.addEventListener('click', (event) => {
    const media = event.target.closest?.(MEDIA_SELECTOR);

    if (!media) return;

    const video = media.querySelector('video');

    if (event.target.closest?.(FULLSCREEN_SELECTOR)) {
      enterFullscreen(video);
      if (!media.playing) media.play(); // Going fullscreen on a still frame is never what is wanted.
      return; // Do not fall through to the play/pause toggle.
    }

    // A non-video file in the metaobject's videos list makes video_tag render nothing. Without this
    // the reconcile below would throw and take the handler down for every reel on the page.
    if (!video) return;

    if (video.controls) {
      const rect = video.getBoundingClientRect();
      const strip = Math.min(CONTROL_STRIP_HEIGHT, rect.height * 0.5);

      // This is necessarily a geometry heuristic: the native control bar lives
      // in UA shadow DOM, so its clicks retarget to video like picture clicks.
      if (event.clientY > rect.bottom - strip) return;
    }

    // Decide the target state now, apply it after the browser's own default action has run.
    // Chrome and Firefox natively toggle play/pause on a click on a <video controls>; toggling
    // again here would cancel that out (pause, then instant replay). Reconciling to a target
    // state is idempotent, so it is correct whether or not the browser acted.
    const shouldPlay = !media.playing;

    requestAnimationFrame(() => {
      if (shouldPlay) {
        if (video.paused) media.play();
      } else if (!video.paused) {
        // Pause via the video itself, not media.pause(): BaseMedia.pause() no-ops when its own
        // playing attribute is out of sync with the real element state. theme.js listens for the
        // video's pause event and clears the attribute, so this stays consistent either way.
        video.pause();
      }
    });
  });

  // On the first click, the theme's one-shot listener also calls play(). This is
  // harmless: BaseMedia guards when already playing, and a duplicate native
  // video play() resolves without error, so the theme handler stays untouched.
})();

/* --- Subscription options and sticky total (BL) — the visible purchase choice is the only selection authority --- */
(() => {
  const RADIO_SELECTOR = '.bl-sub__option-input';
  const PLAN_SELECTOR = '.bl-sub__plan-select';
  const CHOICE_SELECTOR = `${RADIO_SELECTOR}:not([data-group]), ${PLAN_SELECTOR} option`;
  const CHOICE_CONTENT_SELECTOR = '.bl-sub__savings[data-choice-index], .bl-sub__price[data-choice-index], .bl-sub__notes[data-choice-index], .bl-sub__gifts[data-choice-index]';
  const SAVINGS_SELECTOR = '.bl-sub__savings';
  const PRICE_SELECTOR = '.bl-sub__price';
  const OPTION_SELECTOR = '.bl-sub__option';
  // A saving at or below this is not worth advertising, so the label is suppressed exactly as a zero percentage
  // already is. Pricing deliberately ignores this floor: the discounted price and its strike-through still render,
  // so the page never shows more discount than the cart applies. Mirrored in bl_bmsm-tier-bar.liquid.
  const MIN_SAVINGS_PERCENT = 1;

  // Shopify money_format placeholders; the theme exposes the string but no formatter.
  const formatMoney = (cents) => {
    const format = window.themeVariables?.settings?.moneyFormat || '{{amount}}';
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return format;

    const formats = {
      amount: [2, ',', '.'],
      amount_no_decimals: [0, ',', '.'],
      amount_with_comma_separator: [2, '.', ','],
      amount_no_decimals_with_comma_separator: [0, '.', ','],
      amount_with_apostrophe_separator: [2, "'", '.']
    };
    const [precision, thousands, decimal] = formats[match[1]] || formats.amount;
    const parts = (precision ? cents / 100 : Math.round(cents / 100)).toFixed(precision).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return format.replace(match[0], precision ? `${parts[0]}${decimal}${parts[1]}` : parts[0]);
  };

  class BlSubscriptionOptions extends HTMLElement {
    #abortController;

    connectedCallback() {
      this.#abortController?.abort();
      this.#abortController = new AbortController();

      const listenerOptions = { signal: this.#abortController.signal };

      this.addEventListener('change', this.#onChange, listenerOptions);
      document.documentElement.addEventListener('bl:quantity-total-change', this.#onQuantityTotalChange, listenerOptions);
      document.documentElement.addEventListener('bl:request-final-price', this.#onFinalPriceRequest, listenerOptions);
      this.#syncVisibleChoice();
      this.#repaint(this.#getStepperQuantity());
    }

    disconnectedCallback() {
      this.#abortController?.abort();
    }

    getActiveChoice() {
      const radio = this.querySelector('.bl-sub__option-input:checked');
      if (!radio) return null;
      // A grouped row's plan comes only from its <select>, so radio and dropdown cannot disagree.
      if (radio.hasAttribute('data-group')) {
        const opt = radio.closest('.bl-sub__option')?.querySelector('.bl-sub__plan-select')?.selectedOptions?.[0];
        return opt ? { index: opt.dataset.choiceIndex, plan: opt.dataset.sellingPlan || '', el: opt } : null;
      }
      return { index: radio.dataset.choiceIndex, plan: radio.dataset.sellingPlan || '', el: radio };
    }

    #getPicker = () => document.querySelector(`bl-variant-quantity-picker[data-product-id="${CSS.escape(String(this.dataset.productId))}"]`);

    #getStepperQuantity = () => Array.from(this.#getPicker()?.querySelectorAll('.bl-vqp__input') || []).reduce((total, input) => {
      const quantity = Number(input.value);
      return total + (Number.isFinite(quantity) ? Math.max(0, quantity) : 0);
    }, 0);

    #resolveTierPercent = (choice, total) => {
      if (!choice.hasAttribute('data-tier-percentages')) return null;

      const tierQuantities = (this.#getPicker()?.dataset.tierQuantities || '').split(',');
      const tierPercentages = (choice.dataset.tierPercentages || '').split(',');
      let resolvedPercent = null;

      tierQuantities.forEach((quantityValue, index) => {
        if (quantityValue.trim() === '') return;

        const quantity = Number(quantityValue);
        if (!Number.isFinite(quantity) || quantity > total) return;

        const percentageValue = tierPercentages[index];
        if (percentageValue === undefined || percentageValue.trim() === '') {
          resolvedPercent = null;
          return;
        }

        const percentage = Number(percentageValue);
        resolvedPercent = Number.isFinite(percentage) && percentage > 0 ? percentage : null;
      });

      return resolvedPercent;
    };

    #formatPercent = (percent) => Number.isInteger(percent) ? String(percent) : percent.toFixed(1);

    // Which choice a row is DISPLAYING, which is not the same as the active one: an unselected
    // subscription row still shows its dropdown's current plan so both rows' savings can be compared.
    #getRowChoiceIndex = (option) => {
      const select = option.querySelector(PLAN_SELECTOR);
      if (select) return select.selectedOptions?.[0]?.dataset.choiceIndex;
      return option.querySelector(RADIO_SELECTOR)?.dataset.choiceIndex;
    };

    #syncVisibleChoice = () => {
      // Scoped per row, never across rows: hiding by the active choice blanked the unselected row's savings.
      this.querySelectorAll(OPTION_SELECTOR).forEach((option) => {
        const rowIndex = this.#getRowChoiceIndex(option);

        option.querySelectorAll(CHOICE_CONTENT_SELECTOR).forEach((element) => {
          const emptyPrice = element.matches(PRICE_SELECTOR) && !element.querySelector('[data-price-sale]')?.textContent;
          element.hidden = String(element.dataset.choiceIndex) !== String(rowIndex) || emptyPrice;
        });
      });

      const sellingPlanInput = this.querySelector('[data-selling-plan-input]');
      if (sellingPlanInput) sellingPlanInput.value = this.getActiveChoice()?.plan || '';
    };

    #repaint = (stepperQuantity) => {
      const activeChoiceIndex = this.getActiveChoice()?.index;
      let activeCents = null;
      let activeCompareCents = null;
      const picker = this.#getPicker();
      const pickerRows = Array.from(picker?.querySelectorAll('.bl-vqp__row') || []);
      // stepperTotal = Σ stepper quantities across the picker's rows
      // displayBase = stepperTotal > 0 ? Σ(row unit price × row stepper quantity) : single-unit reference price
      // percent(choice) = tier percentage resolved from max(1, stepperTotal), the quantity displayBase prices
      // sale = round(displayBase × (100 - percent) / 100)
      const stepperBase = pickerRows.reduce((total, row) => {
        const unitPrice = Number(row.dataset.unitPrice);
        const quantity = Number(row.querySelector('.bl-vqp__input')?.value);
        return total + (Number.isFinite(unitPrice) && Number.isFinite(quantity) ? unitPrice * Math.max(0, quantity) : 0);
      }, 0);
      // At zero steppers, row one is deliberate: it is the first renderable variant the shopper sees ("1 bag").
      const firstRowUnitPrice = Number(pickerRows[0]?.dataset.unitPrice);
      const displayBase = pickerRows.length > 0
        ? (stepperQuantity > 0 ? stepperBase : firstRowUnitPrice)
        : Number(this.dataset.unitPrice);
      const wiredToBmsm = !this.hasAttribute('data-standalone');
      // At zero steppers displayBase is one bag's price, so the tier is resolved for one bag too; otherwise each
      // choice would show one bag's price without that bag's discount.
      const pricedQuantity = Math.max(1, stepperQuantity);

      this.querySelectorAll(CHOICE_SELECTOR).forEach((choice) => {
        const choiceIndex = choice.dataset.choiceIndex;
        const percentage = this.#resolveTierPercent(choice, pricedQuantity);
        const badge = (choice.dataset.badge || '').trim();
        const savings = this.querySelector(`${SAVINGS_SELECTOR}[data-choice-index="${CSS.escape(String(choiceIndex))}"]`);
        const price = this.querySelector(`${PRICE_SELECTOR}[data-choice-index="${CSS.escape(String(choiceIndex))}"]`);

        if (!savings || !price) return;

        // The badge is only ever shown when this product has no BMSM programme wired. Once tiers exist, the
        // ladder percentage is the sole source, and a choice below its first discounted tier shows nothing.
        // A percentage at or under the floor shows no label at all, and never falls through to the badge branch:
        // once a programme is wired the ladder is the sole source, floor or not.
        if (percentage !== null && percentage <= MIN_SAVINGS_PERCENT) {
          savings.textContent = '';
          savings.classList.remove('bl-sub__badge');
          savings.hidden = true;
        } else if (percentage !== null) {
          savings.textContent = (this.#getPicker()?.dataset.savingsTemplate || 'Save {percent}%')
            .replaceAll('{percent}', this.#formatPercent(percentage));
          savings.classList.remove('bl-sub__badge');
          savings.hidden = false;
        } else if (badge && !wiredToBmsm) {
          savings.textContent = badge;
          savings.classList.add('bl-sub__badge');
          savings.hidden = false;
        } else {
          savings.textContent = '';
          savings.classList.remove('bl-sub__badge');
          savings.hidden = true;
        }

        const sale = price.querySelector('[data-price-sale]');
        const compare = price.querySelector('[data-price-compare]');
        if (!sale || !compare) return;

        price.hidden = false;
        const planPrice = Number(choice.dataset.planPrice);
        // With no picker, Shopify's allocation is the price the plan will actually charge, independent of
        // merchant-entered percentages. Wired BMSM pricing must not use it or the plan discount could apply twice.
        const useAllocationPrice = !wiredToBmsm
          && choice.hasAttribute('data-plan-price')
          && Number.isFinite(planPrice)
          && planPrice > 0
          && planPrice < displayBase;

        if ((percentage === null || percentage <= 0) && !useAllocationPrice) {
          // moneyFormat is shop configuration, not user input, and receives only digits and separators.
          // Preserve its HTML wrapper so the converter app handles these prices like every other page price;
          // stripping the tags would break conversion.
          sale.innerHTML = formatMoney(displayBase);
          if (String(choiceIndex) === String(activeChoiceIndex)) activeCents = displayBase;
          compare.textContent = '';
          compare.hidden = true;
        } else {
          // Round once in cents so the displayed figure cannot drift by a fraction of a unit.
          const discountedCents = useAllocationPrice
            ? planPrice
            : Math.round(displayBase * (100 - percentage) / 100);
          sale.innerHTML = formatMoney(discountedCents);
          if (String(choiceIndex) === String(activeChoiceIndex)) activeCents = discountedCents;
          if (String(choiceIndex) === String(activeChoiceIndex)) activeCompareCents = displayBase;
          compare.innerHTML = formatMoney(displayBase);
          compare.hidden = false;
        }
      });

      // Repainting never dispatches a choice change, which prevents a quantity/choice event loop.
      this.#syncVisibleChoice();

      // The mobile sticky bar needs the same figure this component just computed. Re-deriving it there would
      // duplicate the tier ladder and plan-allocation rules, which is exactly how two prices drift apart.
      if (activeCents !== null) {
        document.documentElement.dispatchEvent(new CustomEvent('bl:final-price-change', {
          bubbles: true,
          detail: {
            productId: this.dataset.productId,
            cents: activeCents,
            compareCents: activeCompareCents,
            // displayBase already multiplies by the stepper quantities in the wired-BMSM case only; anywhere
            // else the figure is per unit and the consumer still has to apply the form quantity.
            quantityIncluded: wiredToBmsm && stepperQuantity > 0,
            stepperQuantity,
            hasPicker: !!this.#getPicker()
          }
        }));
      }
    };

    #onChange = (event) => {
      if (!event.target.matches(RADIO_SELECTOR) && !event.target.matches(PLAN_SELECTOR)) return;

      if (event.target.matches(PLAN_SELECTOR)) {
        const radio = event.target.closest('.bl-sub__option')?.querySelector(RADIO_SELECTOR);
        if (radio && !radio.checked) radio.checked = true;
      }

      const activeChoice = this.getActiveChoice();
      this.#syncVisibleChoice();

      if (activeChoice) {
        document.documentElement.dispatchEvent(new CustomEvent('bl:purchase-choice-change', {
          bubbles: true,
          detail: {
            choiceIndex: activeChoice.index,
            sellingPlan: activeChoice.plan,
            productId: this.dataset.productId
          }
        }));
      }

      this.#repaint(this.#getStepperQuantity());
    };

    #onQuantityTotalChange = (event) => {
      if (String(event.detail?.productId) !== String(this.dataset.productId)) return;

      const stepperQuantity = Number(event.detail.stepperQuantity);
      this.#repaint(Number.isFinite(stepperQuantity) ? Math.max(0, stepperQuantity) : 0);
    };

    // A sticky bar re-rendered by product-rerender connects after this component has already published, so
    // it has to be able to ask for the current figure rather than wait for the next quantity or plan change.
    #onFinalPriceRequest = (event) => {
      if (String(event.detail?.productId) !== String(this.dataset.productId)) return;
      this.#repaint(this.#getStepperQuantity());
    };
  }

  class BlStickyTotal extends HTMLElement {
    #abortController;
    #cents = null;
    #compareCents = null;
    #quantityIncluded = false;
    #stepperQuantity = 0;
    #hasPicker = false;

    connectedCallback() {
      this.#abortController?.abort();
      this.#abortController = new AbortController();

      const listenerOptions = { signal: this.#abortController.signal };

      document.documentElement.addEventListener('bl:final-price-change', this.#onFinalPriceChange, listenerOptions);
      document.documentElement.addEventListener('bl:quantity-total-change', this.#onQuantityTotalChange, listenerOptions);
      document.addEventListener('input', this.#onFormQuantityChange, listenerOptions);
      document.addEventListener('change', this.#onFormQuantityChange, listenerOptions);

      // Ask first: this element is re-created by product-rerender on every variant change, long after the
      // purchase options last published, and rendering from a null figure would flash the undiscounted price.
      document.documentElement.dispatchEvent(new CustomEvent('bl:request-final-price', {
        bubbles: true,
        detail: { productId: this.dataset.productId }
      }));

      this.#render();
    }

    disconnectedCallback() {
      this.#abortController?.abort();
    }

    #getForm = () => document.getElementById(this.getAttribute('form'));

    #getFormQuantity = () => {
      const input = this.#getForm()?.querySelector('input[name="quantity"]');
      if (!input) return 1;

      const quantity = Number(input.value);
      return Number.isFinite(quantity) && quantity > 0 ? quantity : 0;
    };

    #getPicker = () => document.querySelector(`bl-variant-quantity-picker[data-product-id="${CSS.escape(String(this.dataset.productId))}"]`);

    #getPickerSubtotal = () => Array.from(this.#getPicker()?.querySelectorAll('.bl-vqp__row') || []).reduce((total, row) => {
      const unitPrice = Number(row.dataset.unitPrice);
      const quantity = Number(row.querySelector('.bl-vqp__input')?.value);
      return total + (Number.isFinite(unitPrice) && Number.isFinite(quantity) ? unitPrice * Math.max(0, quantity) : 0);
    }, 0);

    // The first renderable row is the "one pack" reference the purchase options use at zero steppers, so
    // the two agree by construction rather than by coincidence.
    #getFirstRowUnitPrice = () => {
      const price = Number(this.#getPicker()?.querySelector('.bl-vqp__row')?.dataset.unitPrice);
      return Number.isFinite(price) ? price : null;
    };

    #render = () => {
      const value = this.querySelector('[data-bl-sticky-total-value]');
      const compare = this.querySelector('[data-bl-sticky-total-compare]');
      const hasPicker = this.#hasPicker || !!this.#getPicker();
      let total = null;
      let compareTotal = null;

      if (hasPicker) {
        // Whatever the purchase options published is already the right figure in both states: the sum of
        // the chosen quantities above zero, the one-pack reference at zero. Only a product with
        // no purchase options at all has to fall back to the picker's own undiscounted numbers.
        if (this.#cents !== null) {
          total = this.#cents;
          compareTotal = this.#compareCents;
        } else {
          total = this.#stepperQuantity > 0 ? this.#getPickerSubtotal() : this.#getFirstRowUnitPrice();
        }
      } else {
        // A quantity of zero still shows a price, so the multiplier floors at one pack.
        const quantity = Math.max(1, this.#getFormQuantity());
        const unitPrice = this.#cents ?? Number(this.dataset.unitPrice);
        const unitCompare = this.#cents !== null ? this.#compareCents : Number(this.dataset.comparePrice);

        if (Number.isFinite(unitPrice)) total = unitPrice * quantity;
        if (Number.isFinite(unitCompare) && unitCompare > unitPrice) compareTotal = unitCompare * quantity;
      }

      if (!Number.isFinite(total)) {
        this.hidden = true;
        return;
      }

      if (value) value.innerHTML = formatMoney(total);

      // The compare-at figure is opt-in per instance: it belongs beside the desktop price but there is no
      // room for a second number inside the mobile button.
      if (compare) {
        const showCompare = this.hasAttribute('data-show-compare') && Number.isFinite(compareTotal) && compareTotal > total;
        compare.innerHTML = showCompare ? formatMoney(compareTotal) : '';
        compare.hidden = !showCompare;
      }

      this.hidden = false;
    };

    #onFinalPriceChange = (event) => {
      if (String(event.detail?.productId) !== String(this.dataset.productId)) return;

      this.#cents = event.detail.cents;
      this.#compareCents = event.detail.compareCents ?? null;
      this.#quantityIncluded = !!event.detail.quantityIncluded;
      this.#stepperQuantity = event.detail.stepperQuantity;
      this.#hasPicker = !!event.detail.hasPicker;
      this.#render();
    };

    #onQuantityTotalChange = (event) => {
      if (String(event.detail?.productId) !== String(this.dataset.productId)) return;

      const stepperQuantity = Number(event.detail.stepperQuantity);
      this.#stepperQuantity = Number.isFinite(stepperQuantity) ? Math.max(0, stepperQuantity) : 0;
      this.#hasPicker = !!this.#getPicker();
      this.#render();
    };

    #onFormQuantityChange = (event) => {
      const quantityInput = this.#getForm()?.querySelector('input[name="quantity"]');
      if (event.target !== quantityInput) return;

      this.#render();
    };
  }

  if (!window.customElements.get('bl-sticky-total')) {
    window.customElements.define('bl-sticky-total', BlStickyTotal);
  }

  (() => {
    const TITLE_SELECTOR = '.bl-sticky-atc__title';

    // Delegated, because product-rerender replaces the sticky bar's markup on every variant change.
    document.addEventListener('click', (event) => {
      const title = event.target.closest?.(TITLE_SELECTOR);

      if (!title) return;

      // A modified or secondary click still belongs to the browser, so opening the product in a new tab works.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

      event.preventDefault();

      const productSection = title.closest('.shopify-section');
      let top = 0;

      if (productSection) {
        // Same offset the picker's reveal uses: --sticky-header-enabled (:root, sections/header.liquid:18) means a
        // non-sticky header contributes nothing, and --header-height is the live measured height, not a guess.
        const rootStyles = getComputedStyle(document.documentElement);
        const stickyHeader = Number(rootStyles.getPropertyValue('--sticky-header-enabled')) === 1;
        const headerHeight = stickyHeader ? parseFloat(rootStyles.getPropertyValue('--header-height')) || 0 : 0;

        top = Math.max(0, window.scrollY + productSection.getBoundingClientRect().top - headerHeight);
      }

      window.scrollTo({
        top,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    });
  })();

  if (!window.customElements.get('bl-subscription-options')) {
    window.customElements.define('bl-subscription-options', BlSubscriptionOptions);
  }
})();

/* --- Variant quantity picker (BL) — add several variants through one cart request --- */
(() => {
  const ROW_SELECTOR = '.bl-vqp__row';
  const INPUT_SELECTOR = '.bl-vqp__input';
  const TOTAL_SELECTOR = '[data-bl-total]';
  const SUBMIT_SELECTOR = '[type="submit"]';

  class BlVariantQuantityPicker extends HTMLElement {
    #submitting = false;
    #abortController;

    connectedCallback() {
      this.#abortController?.abort();
      this.#abortController = new AbortController();

      const listenerOptions = { signal: this.#abortController.signal };

      this.addEventListener('input', this.#onQuantityEvent, listenerOptions);
      this.addEventListener('change', this.#onQuantityEvent, listenerOptions);
      document.addEventListener('submit', this.#onSubmit, { ...listenerOptions, capture: true });
      document.documentElement.addEventListener('bl:purchase-choice-change', this.#onPurchaseChoiceChange, listenerOptions);

      this.#repaint();
    }

    disconnectedCallback() {
      this.#abortController?.abort();
    }

    #getForm = () => document.getElementById(this.getAttribute('form'));

    #getInputs = () => Array.from(this.querySelectorAll(INPUT_SELECTOR));

    #getSubscriptionOptions = () => document.querySelector(`bl-subscription-options[data-product-id="${CSS.escape(String(this.dataset.productId))}"]`);

    #getActiveChoice = () => {
      // Query live at the moment of use: the posted plan must always be the choice currently visible to the shopper.
      const subscriptionOptions = this.#getSubscriptionOptions();
      return subscriptionOptions ? subscriptionOptions.getActiveChoice() : { index: '0', plan: '' };
    };

    #getStepperQuantity = () => this.#getInputs().reduce((total, input) => {
      const quantity = Number(input.value);
      return total + (Number.isFinite(quantity) ? Math.max(0, quantity) : 0);
    }, 0);

    // The sticky add-to-cart bar can be pressed from anywhere on the page, so an empty picker has to say
    // where the missing choice lives rather than just refusing. Reading --sticky-header-enabled (:root,
    // sections/header.liquid:18) means a non-sticky header contributes no offset, and --header-height
    // (set on documentElement at sections/header.liquid:415) is the live measured height, not a guess.
    #revealPicker = () => {
      const rootStyles = getComputedStyle(document.documentElement);
      const stickyHeader = Number(rootStyles.getPropertyValue('--sticky-header-enabled')) === 1;
      const headerHeight = stickyHeader ? parseFloat(rootStyles.getPropertyValue('--header-height')) || 0 : 0;
      const top = Math.max(0, window.scrollY + this.getBoundingClientRect().top - headerHeight - 16);

      window.scrollTo({
        top,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });

      // The card itself is the focus target, not the first stepper: the steppers are type="number", and
      // focusing one would open the numeric keypad over the picker the shopper was just sent to see.
      // preventScroll stops the focus call from jumping the page ahead of the smooth scroll above.
      this.focus({ preventScroll: true });
    };

    #repaint = () => {
      const stepperQuantity = this.#getStepperQuantity();
      const activeChoice = this.#getActiveChoice();

      // The total pill, hearts and purchase-option discounts all use the current stepper selection.
      // Existing cart items do not contribute to this picker's displayed tier.
      const totalElement = this.querySelector(TOTAL_SELECTOR);
      const unit = stepperQuantity === 1 ? this.dataset.unitSingular : this.dataset.unitPlural;

      if (totalElement) totalElement.textContent = `${stepperQuantity} ${unit}`;

      document.documentElement.dispatchEvent(new CustomEvent('bl:quantity-total-change', {
        bubbles: true,
        detail: {
          total: stepperQuantity,
          stepperQuantity,
          choiceIndex: activeChoice?.index,
          sellingPlan: activeChoice?.plan || '',
          productId: this.dataset.productId,
          formId: this.getAttribute('form')
        }
      }));
    };

    #onQuantityEvent = (event) => {
      if (!event.target.matches(INPUT_SELECTOR)) return;

      if (event.type === 'change') {
        const quantity = event.target.valueAsNumber;

        if (!Number.isFinite(quantity) || quantity < 0) event.target.value = '0';
      }

      this.#repaint();
    };

    #onPurchaseChoiceChange = (event) => {
      if (String(event.detail?.productId) !== String(this.dataset.productId)) return;
      this.#repaint();
    };

    #onSubmit = (event) => {
      const form = this.#getForm();

      if (!form || event.target !== form) return;

      event.preventDefault();
      event.stopPropagation();

      this.#addSelectedVariants(form);
    };

    #abortAdd = (form, error, { refresh = false } = {}) => {
      this.#submitting = false;
      form.dispatchEvent(new CustomEvent('cart:error', {
        bubbles: true,
        detail: { error }
      }));

      if (refresh) document.dispatchEvent(new CustomEvent('cart:refresh'));
    };

    #addSelectedVariants = async (form) => {
      if (this.#submitting) return;

      // Checked before the purchase-option lookup: with nothing chosen at all, "select a purchase option"
      // names the wrong problem.
      if (this.#getStepperQuantity() === 0) {
        this.#abortAdd(form, this.dataset.emptyMessage || 'Please choose at least one item.');
        this.#revealPicker();
        return;
      }

      const choice = this.#getActiveChoice();

      if (!choice) {
        // Never assume one-time when the visible purchase-option selection is missing.
        this.#abortAdd(form, 'Please select a purchase option.');
        return;
      }

      const plan = choice.plan;
      const sellingPlan = Number(plan);
      const items = [];

      for (const row of this.querySelectorAll(ROW_SELECTOR)) {
        const input = row.querySelector(INPUT_SELECTOR);
        const quantity = Number(input?.value || 0);

        if (quantity <= 0) continue;

        const id = Number(row.dataset.variantId);
        const allowed = (row.dataset.sellingPlans || '').split(',').filter(Boolean);

        if (plan && (!allowed.includes(plan) || !Number.isFinite(sellingPlan))) {
          // Abort the whole add: omitting the plan would invisibly charge a subscription shopper for a one-time line.
          this.#abortAdd(form, 'The selected subscription is not available for every chosen item.');
          return;
        }

        // One-time lines omit selling_plan entirely; null, zero, and empty strings are not equivalent cart requests.
        items.push(plan ? { id, quantity, selling_plan: sellingPlan } : { id, quantity });
      }

      if (items.length === 0) {
        this.#abortAdd(form, this.dataset.emptyMessage || 'Please choose at least one item.');
        this.#revealPicker();
        return;
      }

      const submitButtons = Array.from(form.querySelectorAll(SUBMIT_SELECTOR));
      const sectionsToBundle = ['variant-added'];

      submitButtons.forEach((button) => button.setAttribute('aria-busy', 'true'));
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      }));

      this.#submitting = true;

      try {
        // Shopify's multi-item endpoint reliably parses the nested items array from JSON, not multipart FormData.
        const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items,
            sections: sectionsToBundle.join(','),
            sections_url: window.location.pathname
          })
        });
        const responseJson = await response.json();

        if (!response.ok) {
          this.#abortAdd(form, responseJson.description, { refresh: true });
          return;
        }

        if (window.themeVariables.settings.cartType === 'page' || window.themeVariables.settings.pageType === 'cart') {
          window.location.href = `${Shopify.routes.root}cart`;
          return;
        }

        const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        cart.sections = responseJson.sections;

        form.dispatchEvent(new CustomEvent('variant:add', {
          bubbles: true,
          detail: {
            items: responseJson.hasOwnProperty('items') ? responseJson.items : [responseJson],
            cart
          }
        }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'variant:add',
            cart
          }
        }));

        // Clear the completed selection and reset its total, hearts and prices for the next add.
        this.#getInputs().forEach((input) => {
          input.value = '0';
        });
        this.#repaint();
      } catch (error) {
        this.#abortAdd(form, error.message, { refresh: true });
      } finally {
        submitButtons.forEach((button) => button.removeAttribute('aria-busy'));
        this.#submitting = false;
      }
    };
  }

  if (!window.customElements.get('bl-variant-quantity-picker')) {
    window.customElements.define('bl-variant-quantity-picker', BlVariantQuantityPicker);
  }
})();

/* ---------------------------------------------------------------------------
   Sticky header (BL) — bottom border state
   Flags the sticky store-header only once its section reaches its own CSS top
   offset, so announcement-bar height and other content above need no hardcoded
   scroll threshold. The CSS decides when that state is paintable.
--------------------------------------------------------------------------- */
(() => {
  const HEADER_SELECTOR = 'store-header[sticky]';
  const STUCK_CLASS = 'bl-header-stuck';
  const EPSILON = 0.5; // absorbs sub-pixel layout rounding
  let header = null;
  let section = null;
  let stickyTop = 0;
  let pendingFrame = null;
  let isFilled = false;

  // `.is-filled` flips a transparent header section from relative to sticky;
  // remembering its state prevents this block's own class toggle from re-reading.
  const headerObserver = new MutationObserver(() => {
    const nextIsFilled = header?.classList.contains('is-filled') || false;

    if (nextIsFilled === isFilled) return;

    isFilled = nextIsFilled;
    readStickyTop();
  });

  const resolveHeader = () => {
    const nextHeader = document.querySelector(HEADER_SELECTOR);

    headerObserver.disconnect();
    if (header && header !== nextHeader) header.classList.remove(STUCK_CLASS);

    header = nextHeader;
    section = header?.closest('.shopify-section') || null;
    isFilled = header?.classList.contains('is-filled') || false;

    if (header) headerObserver.observe(header, { attributes: true, attributeFilter: ['class'] });
  };

  const update = () => {
    if (!header || !section || !header.isConnected || !section.isConnected) return;

    // A sticky box stops moving when its rect reaches its computed top offset.
    // scrollY excludes the at-rest top position and menu-open fills at scroll 0.
    const isStuck = window.scrollY > 0 && section.getBoundingClientRect().top <= stickyTop + EPSILON;
    header.classList.toggle(STUCK_CLASS, isStuck);
  };

  const scheduleUpdate = () => {
    if (pendingFrame !== null) return;

    pendingFrame = requestAnimationFrame(() => {
      pendingFrame = null;
      update();
    });
  };

  const readStickyTop = () => {
    if (!header || !section || !header.isConnected || !section.isConnected) return;

    // This value depends on the announcement bar's post-layout height. Read it
    // after layout changes, never in the high-frequency scroll path.
    const computedTop = parseFloat(getComputedStyle(section).top);
    stickyTop = Number.isFinite(computedTop) ? computedTop : 0;
    scheduleUpdate();
  };

  const refreshHeader = () => {
    // The theme editor replaces sections in place, so discard stale references.
    resolveHeader();
    readStickyTop();
  };

  resolveHeader();
  readStickyTop();

  window.addEventListener('scroll', scheduleUpdate, { passive: true });
  window.addEventListener('resize', readStickyTop);
  window.addEventListener('load', readStickyTop);
  document.addEventListener('shopify:section:load', refreshHeader);
})();

/* --- Buy more, save more tier bar (BL) — mirror the product quantity total without owning pricing --- */
(() => {
  const TIER_SELECTOR = '.bl-tier-bar__tier';
  const SAVINGS_SELECTOR = '.bl-tier-bar__savings[data-choice-index]';

  class BlBmsmTierBar extends HTMLElement {
    #abortController;

    connectedCallback() {
      this.#abortController?.abort();
      this.#abortController = new AbortController();

      document.documentElement.addEventListener('bl:quantity-total-change', this.#onTotalChange, {
        signal: this.#abortController.signal
      });
    }

    disconnectedCallback() {
      this.#abortController?.abort();
    }

    #onTotalChange = (event) => {
      if (String(event.detail?.productId) !== String(this.dataset.productId)) return;

      this.querySelectorAll(SAVINGS_SELECTOR).forEach((label) => {
        label.hidden = String(label.dataset.choiceIndex) !== String(event.detail.choiceIndex);
      });
      this.#applyTotal(Number(event.detail.total));
    };

    #applyTotal = (total) => {
      // Section re-renders can replace the tier markup without reconnecting every related element.
      const tiers = Array.from(this.querySelectorAll(TIER_SELECTOR));
      let currentTier = null;
      let highestReachedQuantity = 0;

      tiers.forEach((tier) => {
        const minQuantity = Number(tier.dataset.minQuantity);
        const reached = Number.isFinite(total) && Number.isFinite(minQuantity) && total >= minQuantity;

        tier.classList.toggle('is-reached', reached);
        tier.classList.remove('is-current');
        tier.removeAttribute('aria-current');

        if (reached && minQuantity > highestReachedQuantity) {
          highestReachedQuantity = minQuantity;
          currentTier = tier;
        }
      });

      if (currentTier) {
        currentTier.classList.add('is-current');
        currentTier.setAttribute('aria-current', 'step');
      }
    };
  }

  if (!window.customElements.get('bl-bmsm-tier-bar')) {
    window.customElements.define('bl-bmsm-tier-bar', BlBmsmTierBar);
  }
})();

/* ---------------------------------------------------------------------------
   About Founder (BL) — independent scroll reveals
   The theme's reveal-on-scroll attribute is not generic: theme.css:9200 hides
   it, while only custom elements wired to getHeadingKeyframe (theme.js:447)
   reveal it again. reveal-items (theme.js:474) depends on the merchant's
   product-grid stagger setting, and split-lines (theme.js:897) renders into a
   shadow root our stylesheet cannot reach. This observer keeps the About page
   animation independent from Motion, theme settings, and those mechanisms.
--------------------------------------------------------------------------- */
(() => {
  const REVEAL_SELECTOR = '[bl-reveal]';
  const REVEALED_CLASS = 'bl-is-revealed';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  const tracked = new WeakSet();

  const reveal = (element) => element.classList.add(REVEALED_CLASS);
  const revealAll = (root) => root.querySelectorAll(REVEAL_SELECTOR).forEach(reveal);

  if (window.matchMedia(REDUCED_MOTION_QUERY).matches || !window.IntersectionObserver) {
    revealAll(document);
    document.addEventListener('shopify:section:load', (event) => revealAll(event.target));
    return;
  }

  try {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        reveal(entry.target);
        observer.unobserve(entry.target); // One-way reveal: scrolling back up must not replay it.
        tracked.delete(entry.target);
      });

      stopSafetyScrollWhenComplete();
    }, {
      // The negative bottom margin is a cosmetic trigger preference, so it must never be the only
      // path to visibility. It keeps stacked elements' staggered delays reading as one gesture.
      rootMargin: '0px 0px -12% 0px',
      threshold: 0
    });

    let scrollController = null;
    let pendingSweepFrame = null;

    const hasUnrevealedElements = () => document.querySelector(`${REVEAL_SELECTOR}:not(.${REVEALED_CLASS})`);

    const stopSafetyScrollWhenComplete = () => {
      if (hasUnrevealedElements()) return;

      scrollController?.abort();
      scrollController = null;
    };

    const revealVisibleElements = () => {
      document.querySelectorAll(`${REVEAL_SELECTOR}:not(.${REVEALED_CLASS})`).forEach((element) => {
        const rect = element.getBoundingClientRect();
        if (rect.top >= window.innerHeight || rect.bottom <= 0) return;

        reveal(element);
        observer.unobserve(element);
        tracked.delete(element);
      });

      stopSafetyScrollWhenComplete();
    };

    const scheduleSafetySweep = () => {
      if (pendingSweepFrame !== null) return;

      pendingSweepFrame = requestAnimationFrame(() => {
        pendingSweepFrame = null;
        revealVisibleElements();
      });
    };

    const startSafetyScroll = () => {
      if (scrollController || !hasUnrevealedElements()) return;

      scrollController = new AbortController();
      window.addEventListener('scroll', scheduleSafetySweep, {
        passive: true,
        signal: scrollController.signal
      });
    };

    const scan = (root) => {
      root.querySelectorAll(REVEAL_SELECTOR).forEach((element) => {
        if (tracked.has(element)) return;

        tracked.add(element);
        observer.observe(element);
      });

      startSafetyScroll();
    };

    scan(document);
    window.addEventListener('load', revealVisibleElements, { once: true });

    // Theme editor: sections are re-rendered in place.
    document.addEventListener('shopify:section:load', (event) => scan(event.target));
  } catch (error) {
    // An animation is never worth risking invisible content, including after an editor re-render.
    revealAll(document);
    document.addEventListener('shopify:section:load', (event) => revealAll(event.target));
  }
})();

/* ---------------------------------------------------------------------------
   Text image slider (BL) — paired carousel state
   The fixed heading and dots sit outside the crossfade, so body copy and image
   live in separate grid-column carousels. The theme cannot link both to one
   page-dots control because page-dots targets one aria-controls id.
--------------------------------------------------------------------------- */
(() => {
  const CAROUSEL_SELECTOR = 'effect-carousel';
  const CHANGE_EVENT = 'carousel:change';

  class BlCarouselSync extends HTMLElement {
    #carousels = [];
    #syncing = false;
    #abortController;

    connectedCallback() {
      this.#abortController?.abort();
      this.#abortController = new AbortController();
      this.#carousels = Array.from(this.querySelectorAll(CAROUSEL_SELECTOR));

      if (this.#carousels.length < 2) return;

      this.addEventListener(CHANGE_EVENT, this.#onCarouselChange, {
        signal: this.#abortController.signal
      });
    }

    disconnectedCallback() {
      this.#abortController?.abort();
    }

    #onCarouselChange = (event) => {
      if (this.#syncing) return;

      const index = event.detail.index;
      this.#syncing = true;

      // EffectCarousel.select() dispatches carousel:change synchronously before awaiting its fade,
      // so this guard must cover the entire loop to prevent the paired carousels from ping-ponging.
      this.#carousels.forEach((carousel) => {
        if (carousel === event.target || carousel.selectedIndex === index) return;

        carousel.select(index);
      });

      this.#syncing = false;
    };
  }

  if (!window.customElements.get('bl-carousel-sync')) window.customElements.define('bl-carousel-sync', BlCarouselSync);
})();

/* ---------------------------------------------------------------------------
   CSR Program (BL) — progressive mobile copy clamp
--------------------------------------------------------------------------- */
(() => {
  const BREAKPOINT = 1000;
  const ENTRY_SELECTOR = '.bl-csr__entry';
  const BODY_SELECTOR = '.bl-csr__body';
  const CONTROL_SELECTOR = '.bl-csr__more';
  let pendingFrame = null;

  const measureEntry = (entry) => {
    const body = entry.querySelector(BODY_SELECTOR);
    const control = entry.querySelector(CONTROL_SELECTOR);
    if (!body || !control) return;

    if (window.innerWidth >= BREAKPOINT) {
      entry.classList.remove('is-clampable', 'is-open');
      control.setAttribute('aria-expanded', 'false');
      return;
    }

    if (entry.classList.contains('is-open')) {
      // Never expose an open control without the clampable state it can collapse back into.
      entry.classList.add('is-clampable');
      control.setAttribute('aria-expanded', 'true');
      return;
    }

    // Script owns this class so no-JavaScript visitors always receive the complete copy.
    entry.classList.add('is-clampable');

    // The 1px tolerance absorbs sub-pixel line heights at browser zoom levels.
    if (body.scrollHeight <= body.clientHeight + 1) entry.classList.remove('is-clampable');

    entry.classList.remove('is-open');
    control.setAttribute('aria-expanded', 'false');
  };

  const measureEntries = () => document.querySelectorAll(ENTRY_SELECTOR).forEach(measureEntry);

  const scheduleMeasurement = () => {
    if (pendingFrame !== null) return;

    pendingFrame = requestAnimationFrame(() => {
      pendingFrame = null;
      measureEntries();
    });
  };

  document.addEventListener('click', (event) => {
    const control = event.target.closest(CONTROL_SELECTOR);
    if (!control) return;

    const entry = control.closest(ENTRY_SELECTOR);
    if (!entry || !entry.querySelector(BODY_SELECTOR)) return;

    if (window.innerWidth >= BREAKPOINT) {
      measureEntry(entry);
      return;
    }

    entry.classList.add('is-clampable');
    const isOpen = entry.classList.toggle('is-open');
    control.setAttribute('aria-expanded', String(isOpen));
  });

  measureEntries();
  window.addEventListener('resize', scheduleMeasurement, { passive: true });
  document.addEventListener('shopify:section:load', measureEntries);
})();

/* ---------------------------------------------------------------------------
   Product cards (BL) — reserve optional rows per visual grid row
   Measuring each row's rendered optional content keeps alignment correct for any
   swatch style, label count, or font scale; CSS constants remain the no-JS fallback.
--------------------------------------------------------------------------- */
(() => {
  const LIST_SELECTOR = '.product-list';
  const INFO_SELECTOR = '.product-card__info';
  const LABEL_SELECTOR = '.bl-product-card__promo-label-list';
  const ASIDE_SELECTOR = '.product-card__aside';
  const LABEL_RESERVE_PROPERTY = '--bl-row-label-reserve';
  const ASIDE_RESERVE_PROPERTY = '--bl-row-aside-reserve';
  const observedLists = new WeakSet();
  let pendingFrame = null;

  const setReserve = (info, property, target) => {
    if (info.style.getPropertyValue(property) === target) return;

    info.style.setProperty(property, target);
  };

  const measureOuterHeight = (element) => {
    if (!element) return 0;

    const marginBlockStart = parseFloat(getComputedStyle(element).marginBlockStart) || 0;
    return element.getBoundingClientRect().height + marginBlockStart;
  };

  const updateList = (list) => {
    // Read every offset and height before changing styles so writes cannot reflow later reads.
    const cards = Array.from(list.children).map((card) => ({
      card,
      info: card.querySelector(INFO_SELECTOR)
    })).filter(({ card, info }) => info && card.offsetParent !== null).map(({ card, info }) => ({
      info,
      offsetTop: card.offsetTop,
      labelHeight: measureOuterHeight(card.querySelector(LABEL_SELECTOR)),
      asideHeight: measureOuterHeight(card.querySelector(ASIDE_SELECTOR))
    }));
    const rows = new Map();

    cards.forEach((entry) => {
      const row = rows.get(entry.offsetTop) || [];
      row.push(entry);
      rows.set(entry.offsetTop, row);
    });

    rows.forEach((row) => {
      const labelHeight = Math.max(0, ...row.map((entry) => entry.labelHeight));
      const asideHeight = Math.max(0, ...row.map((entry) => entry.asideHeight));
      const labelTarget = `${Math.round(labelHeight * 100) / 100}px`;
      const asideTarget = `${Math.round(asideHeight * 100) / 100}px`;

      row.forEach(({ info }) => {
        setReserve(info, LABEL_RESERVE_PROPERTY, labelTarget);
        setReserve(info, ASIDE_RESERVE_PROPERTY, asideTarget);
      });
    });
  };

  const observer = new MutationObserver((mutations) => {
    const changedLists = new Set(mutations.map(({ target }) => target));
    changedLists.forEach(updateList);
  });

  const scan = (root) => {
    root.querySelectorAll(LIST_SELECTOR).forEach((list) => {
      updateList(list);

      if (observedLists.has(list)) return;

      observedLists.add(list);
      observer.observe(list, { childList: true });
    });
  };

  const scheduleScan = () => {
    if (pendingFrame !== null) return;

    pendingFrame = requestAnimationFrame(() => {
      pendingFrame = null;
      scan(document);
    });
  };

  scan(document);
  window.addEventListener('load', () => scan(document));

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => scan(document));
  }

  window.addEventListener('resize', scheduleScan, { passive: true });
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();

/* ---------------------------------------------------------------------------
   Announcement countdown (BL) — one clock for every active timer
   The section's synchronous script removes already-expired arrow slides before
   theme.js can cache them. This loop owns subsequent ticks and expiries.
--------------------------------------------------------------------------- */
(() => {
  const countdowns = new Set();
  let tickInterval = null;

  const pad = (value) => String(value).padStart(2, '0');

  const getEndsAt = (countdown) => {
    const endsAt = Number(countdown.dataset.endsAt) * 1000;
    return Number.isFinite(endsAt) && endsAt > 0 ? endsAt : null;
  };

  // Hours accumulate past 24 instead of rolling into a day unit, so the rendered string is always
  // HH:MM:SS. A single shape means the width reserved in bl_custom.css.liquid never has to absorb a
  // format change, which is what keeps the marquee from shifting when the format would have flipped.
  const formatRemaining = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    return `${pad(hours)}:${pad(minutes)}:${pad(remainingSeconds)}`;
  };

  const syncLoop = () => {
    if (countdowns.size > 0 && tickInterval === null) {
      tickInterval = window.setInterval(tick, 1000);
    } else if (countdowns.size === 0 && tickInterval !== null) {
      window.clearInterval(tickInterval);
      tickInterval = null;
    }
  };

  const reconcileCarousel = (section) => {
    const carousel = section?.querySelector('.announcement-bar__static-list');
    if (!carousel) return;

    const messages = Array.from(carousel.children);
    carousel._items = null;
    carousel._selectedIndex = 0;

    messages.forEach((message, index) => {
      message.removeAttribute('style');
      message.classList.toggle('reveal-invisible', index !== 0);
      message.classList.toggle('is-selected', index === 0);
    });

    if (messages.length === 0) {
      carousel.player?.pause();
      section.hidden = true;
      return;
    }

    if (messages.length === 1) {
      carousel.player?.pause();
      const wrapper = carousel.closest('.announcement-bar__wrapper');
      wrapper?.classList.remove('justify-between');
      wrapper?.classList.add('justify-center');
      wrapper?.querySelectorAll('button[is="prev-button"], button[is="next-button"]').forEach((button) => {
        button.hidden = true;
      });
    }
  };

  const expire = (countdown) => {
    countdowns.delete(countdown);

    const section = countdown.closest('.shopify-section--announcement-bar');
    const message = countdown.closest('.announcement-bar__static-list > p');

    if (message && countdown.dataset.hideMessage === 'true') {
      message.remove();
      reconcileCarousel(section);
    } else if (message && countdown.dataset.expiredText) {
      countdown.textContent = countdown.dataset.expiredText;
      countdown.removeAttribute('data-ends-at');
      countdown.setAttribute('data-expired', '');
    } else {
      const bar = countdown.closest('.announcement-bar');
      const isPill = countdown.classList.contains('bl-announcement-countdown--pill');
      countdown.remove();

      if (isPill) bar?.classList.remove('bl-announcement--has-countdown');
    }

    syncLoop();
  };

  function tick() {
    const now = Date.now();

    Array.from(countdowns).forEach((countdown) => {
      const endsAt = getEndsAt(countdown);

      if (endsAt === null || endsAt <= now) {
        expire(countdown);
        return;
      }

      const digits = countdown.querySelector('.bl-announcement-countdown__digits');
      if (digits) digits.textContent = formatRemaining(Math.ceil((endsAt - now) / 1000));
    });
  }

  const register = (countdown) => {
    if (countdowns.has(countdown) || getEndsAt(countdown) === null) return;

    countdowns.add(countdown);
    tick();
    syncLoop();
  };

  class BlCountdown extends HTMLElement {
    connectedCallback() {
      register(this);
    }

    disconnectedCallback() {
      countdowns.delete(this);
      syncLoop();
    }
  }

  if (!window.customElements.get('bl-countdown')) {
    window.customElements.define('bl-countdown', BlCountdown);
  }

  const scan = (root) => {
    if (root.matches?.('bl-countdown')) register(root);
    root.querySelectorAll?.('bl-countdown').forEach(register);
  };

  scan(document);
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();

/* ---------------------------------------------------------------------------
   FAQ navigation (BL) — sibling section discovery and scroll state
   Liquid cannot enumerate neighboring sections, so each FAQ advertises its
   label in markup and this element builds the ordered navigation client-side.
--------------------------------------------------------------------------- */
(() => {
  const TARGET_SELECTOR = '[data-bl-faq-nav-label]';
  const READY_CLASS = 'bl-faq-nav-ready';
  const OFFSET_PROPERTY = '--bl-faq-nav-offset';
  const START_CLASS = 'bl-can-scroll-start';
  const END_CLASS = 'bl-can-scroll-end';
  const THRESHOLD = 1; // absorbs sub-pixel rounding
  const ACTIVE_TOLERANCE = 4; // a section resting exactly on the active line still counts as reached

  class BlFaqNav extends HTMLElement {
    #list;
    #targets = [];
    #abortController;
    #resizeObserver;
    #activeFrame = null;
    #rebuildFrame = null;
    #scrollSettleTimer = null;
    #pendingTargetId = null;
    #rowSettleTimer = null;
    #lastRequestedRowTarget = null;

    connectedCallback() {
      this.#abortController?.abort();
      this.#resizeObserver?.disconnect();
      this.#abortController = new AbortController();
      this.#list = this.querySelector('.bl-faq-nav__list');

      this.#build();

      if (!this.#list) return;

      const listenerOptions = { signal: this.#abortController.signal };
      const passiveListenerOptions = { passive: true, signal: this.#abortController.signal };
      const wheelListenerOptions = { passive: false, signal: this.#abortController.signal };

      this.#list.addEventListener('click', this.#onClick, listenerOptions);
      this.#list.addEventListener('scroll', this.#updateScrollState, passiveListenerOptions);
      this.#list.addEventListener('wheel', this.#onWheel, wheelListenerOptions);
      window.addEventListener('scroll', this.#onWindowScroll, passiveListenerOptions);
      window.addEventListener('resize', this.#onResize, passiveListenerOptions);
      window.addEventListener('load', this.#scheduleRebuild, listenerOptions);
      document.addEventListener('shopify:section:load', this.#scheduleRebuild, listenerOptions);
      document.addEventListener('shopify:section:unload', this.#scheduleRebuild, listenerOptions);
      document.addEventListener('shopify:section:reorder', this.#scheduleRebuild, listenerOptions);

      this.#resizeObserver = new ResizeObserver(this.#onObservedResize);
      this.#resizeObserver.observe(this.closest('.shopify-section') ?? this);
      this.#resizeObserver.observe(this.#list);
      this.#publishOffset();
      this.#updateActive();
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#resizeObserver?.disconnect();

      if (this.#activeFrame !== null) cancelAnimationFrame(this.#activeFrame);
      if (this.#rebuildFrame !== null) cancelAnimationFrame(this.#rebuildFrame);
      if (this.#scrollSettleTimer !== null) clearTimeout(this.#scrollSettleTimer);
      if (this.#rowSettleTimer !== null) clearTimeout(this.#rowSettleTimer);

      this.#rowSettleTimer = null;
      this.#activeFrame = null;
      this.#rebuildFrame = null;
      this.#scrollSettleTimer = null;
      this.#pendingTargetId = null;
      this.#lastRequestedRowTarget = null;
      this.#clearOffset();
    }

    targets() {
      return Array.from(document.querySelectorAll(TARGET_SELECTOR)).map((element) => ({
        id: element.closest('.shopify-section')?.id,
        label: element.getAttribute('data-bl-faq-nav-label')
      })).filter(({ id, label }) => id && label);
    }

    #build() {
      this.#targets = this.targets();

      if (this.#pendingTargetId && !this.#targets.some(({ id }) => id === this.#pendingTargetId)) {
        this.#pendingTargetId = null;

        if (this.#scrollSettleTimer !== null) clearTimeout(this.#scrollSettleTimer);
        this.#scrollSettleTimer = null;
      }

      if (!this.#list) return;

      const section = this.closest('.shopify-section');

      if (this.#targets.length < 2) {
        this.#list.replaceChildren();
        section?.classList.remove(READY_CLASS);
        this.#clearOffset();
        this.#updateScrollState();
        return;
      }

      const fragment = document.createDocumentFragment();

      this.#targets.forEach(({ id, label }) => {
        const chip = document.createElement('a');
        chip.className = 'bl-faq-nav__item';
        chip.setAttribute('href', `#${id}`);
        chip.textContent = label;
        fragment.append(chip);
      });

      this.#list.replaceChildren(fragment);
      section?.classList.add(READY_CLASS);
      this.#updateScrollState();
    }

    #onClick = (event) => {
      const chip = event.target.closest('.bl-faq-nav__item');
      if (!chip || !this.#list.contains(chip)) return;

      event.preventDefault();

      const id = chip.getAttribute('href')?.slice(1);
      const target = id && document.getElementById(id);
      if (!target) return;

      this.#pendingTargetId = id;
      this.#updateActive();

      const rowTarget = this.#chipScrollTarget(chip);
      if (rowTarget !== null) this.#scrollRowTo(rowTarget);

      this.#armScrollSettleTimer();

      // The theme sets no smooth scroll behavior, so a native anchor would teleport. scrollIntoView supplies the
      // animation while honoring html's scroll-padding-block-start (theme.css:13) and the FAQ's scroll margin.
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });

      // Shopify section ids are implementation details, so do not leak them into the URL or shared links as a hash.
    };

    #publishOffset = () => {
      const section = this.closest('.shopify-section');

      if (this.dataset.sticky !== 'true' || !section?.classList.contains(READY_CLASS)) {
        this.#clearOffset();
        return;
      }

      // The Shopify section wrapper is the pinned box: it carries position: sticky and contains the padded
      // .section generated via section-properties. Its height is constant now that padding no longer varies with
      // scroll position, while offsetHeight excludes the resting margins so they do not inflate the published
      // offset. Measuring this custom element instead would under-report the box by the section's padding.
      document.documentElement.style.setProperty(OFFSET_PROPERTY, `${section.offsetHeight + 8}px`);
    };

    #clearOffset() {
      document.documentElement.style.removeProperty(OFFSET_PROPERTY);
    }

    #updateScrollState = () => {
      if (!this.#list) return;

      const overflow = this.#list.scrollWidth - this.#list.clientWidth;
      const scrollable = overflow > THRESHOLD;

      this.#armRowSettleTimer();

      this.classList.toggle(START_CLASS, scrollable && this.#list.scrollLeft > THRESHOLD);
      this.classList.toggle(END_CLASS, scrollable && this.#list.scrollLeft < overflow - THRESHOLD);
    };

    #onWheel = (event) => {
      const overflow = this.#list.scrollWidth - this.#list.clientWidth;

      if (overflow <= THRESHOLD || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;

      event.preventDefault();
      this.#lastRequestedRowTarget = null;
      // CSS smooth scrolling remains useful for active-chip corrections, but animating every wheel tick would lag
      // behind the pointer. `instant` bypasses it for this direct manipulation only.
      this.#list.scrollTo({ left: this.#list.scrollLeft + event.deltaY, behavior: 'instant' });
    };

    #onObservedResize = () => {
      this.#publishOffset();
      this.#updateScrollState();
    };

    #onResize = () => {
      this.#publishOffset();
      this.#updateScrollState();
      this.#scheduleActiveUpdate();
    };

    #onWindowScroll = () => {
      this.#scheduleActiveUpdate();

      if (this.#pendingTargetId) this.#armScrollSettleTimer();
    };

    #armScrollSettleTimer() {
      if (this.#scrollSettleTimer !== null) clearTimeout(this.#scrollSettleTimer);

      this.#scrollSettleTimer = setTimeout(() => {
        this.#scrollSettleTimer = null;
        this.#pendingTargetId = null;
        this.#updateActive();
      }, 150);
    }

    #chipScrollTarget(chip) {
      const chipRect = chip.getBoundingClientRect();
      const listRect = this.#list.getBoundingClientRect();
      const listStyle = getComputedStyle(this.#list);
      const padLeft = parseFloat(listStyle.scrollPaddingLeft) || 0;
      const padRight = parseFloat(listStyle.scrollPaddingRight) || 0;
      const chipStart = chipRect.left - listRect.left + this.#list.scrollLeft;
      const chipEnd = chipStart + chipRect.width;
      const visibleStart = this.#list.scrollLeft + padLeft;
      const visibleEnd = this.#list.scrollLeft + this.#list.clientWidth - padRight;

      // Already fully in view: the shortest distance is none at all, so the row is left where the visitor put it.
      if (chipStart >= visibleStart && chipEnd <= visibleEnd) return null;

      // Otherwise align the chip's own start to the scroll-padding inset. That is deliberately NOT the smallest
      // nudge that would reveal it: .scroll-area sets scroll-snap-type: x mandatory (theme.css:9429), which snaps
      // programmatic scrolls too, so a small nudge is pulled back to the nearest snap point — the PREVIOUS chip's
      // edge — and the row visibly does not move. Measured at 1400px: a 15px nudge to reveal the seventh tab
      // resolved to scrollLeft 0 and left the tab still cut off. Because the chips carry scroll-snap-align: start,
      // this position IS a snap point, so the browser agrees with it and the chip is actually revealed.
      return chipStart - padLeft;
    }

    // Releases the dedupe latch once the row stops moving, whatever position it settled at. Keying the release on
    // the row ARRIVING at the requested target instead latches forever whenever scroll-snap-type: x mandatory lands
    // it somewhere else, which silently froze every later request: measured at 1400px, a click that should have
    // revealed the seventh tab moved the row 0px because the latch still held a target the snap had overridden.
    #armRowSettleTimer = () => {
      if (this.#rowSettleTimer !== null) clearTimeout(this.#rowSettleTimer);

      this.#rowSettleTimer = setTimeout(() => {
        this.#rowSettleTimer = null;
        this.#lastRequestedRowTarget = null;
      }, 120);
    };

    #scrollRowTo(target) {
      const overflow = this.#list.scrollWidth - this.#list.clientWidth;
      if (overflow <= THRESHOLD) return;

      const clamped = Math.min(Math.max(target, 0), overflow);
      if (this.#lastRequestedRowTarget !== null
        && Math.abs(clamped - this.#lastRequestedRowTarget) <= 1) return;

      this.#lastRequestedRowTarget = clamped;
      // Armed here as well as on the row's scroll events, because a request the browser declines to act on emits no
      // scroll event at all and would otherwise leave the latch held.
      this.#armRowSettleTimer();
      // Assigning during an in-flight animation makes the browser retarget from the row's current position, so the
      // carousel always takes the shortest path from where it actually is instead of restarting from an old origin.
      this.#list.scrollLeft = clamped;
    }

    #scheduleActiveUpdate = () => {
      if (this.#activeFrame !== null) return;

      this.#activeFrame = requestAnimationFrame(() => {
        this.#activeFrame = null;
        this.#updateActive();
      });
    };

    #scheduleRebuild = () => {
      if (this.#rebuildFrame !== null) return;

      this.#rebuildFrame = requestAnimationFrame(() => {
        this.#rebuildFrame = null;
        this.#build();
        this.#publishOffset();
        this.#updateActive();
      });
    };

    #updateActive() {
      if (!this.#list || this.#targets.length < 2) return;

      // A scrolled-to section comes to rest at html's scroll-padding-block-start (theme.css:13) plus its own
      // scroll-margin, which bl_faq.liquid takes from --bl-faq-nav-offset. Deriving the active line from those same
      // two numbers is what keeps "the tab you clicked" and "the tab that highlights" in agreement. Measuring the
      // pinned bar's own bottom edge instead looks equivalent but sits ~28px above where the section actually lands,
      // which left the clicked tab unhighlighted and the previous one still active.
      let activeId = this.#pendingTargetId;

      if (!activeId) {
        const rootStyle = getComputedStyle(document.documentElement);
        const scrollPadding = parseFloat(rootStyle.scrollPaddingBlockStart) || 0;
        const navOffset = parseFloat(rootStyle.getPropertyValue(OFFSET_PROPERTY)) || 0;
        const threshold = scrollPadding + navOffset + ACTIVE_TOLERANCE;
        activeId = this.#targets[0].id;

        this.#targets.forEach(({ id }) => {
          const target = document.getElementById(id);
          if (target && target.getBoundingClientRect().top <= threshold) activeId = id;
        });
      }

      const chips = Array.from(this.#list.querySelectorAll('.bl-faq-nav__item'));
      let activeChip = null;

      chips.forEach((chip) => {
        const isActive = chip.getAttribute('href') === `#${activeId}`;
        chip.classList.toggle('is-active', isActive);

        if (isActive) {
          chip.setAttribute('aria-current', 'true');
          activeChip = chip;
        } else {
          chip.removeAttribute('aria-current');
        }
      });

      if (!activeChip || this.#pendingTargetId) return;

      // Adjust only the row's horizontal position. scrollIntoView on a chip would also move the page vertically and
      // fight the visitor's section scroll.
      const target = this.#chipScrollTarget(activeChip);
      if (target !== null) this.#scrollRowTo(target);
    }
  }

  if (!window.customElements.get('bl-faq-nav')) window.customElements.define('bl-faq-nav', BlFaqNav);
})();

/* --- Variant card quick add (BL) — one tap adds the card's variant and confirms in place ----------------------------
   A variant card fixes its variant, so the quick-buy drawer had nothing left to ask: it fetched the whole product page
   only for the shopper to press Add a second time. The card now posts the add through the theme's own product-form
   element, and this listener sends the outcome to the same "Added to your cart" panel the drawer flow used to end on
   instead of to the cart drawer.
------------------------------------------------------------------------------------------------------------------- */
(() => {
  const FORM_ID_PREFIX = 'bl-quick-add-';
  const DRAWER_ID_SUFFIX = '-drawer';

  const showConfirmation = (drawer, section) => {
    drawer.replaceChildren(...section.children);
    // .quick-buy-drawer is display:none in theme.css until the element's own show() sets the inline style, which is
    // what QuickBuyDrawer does too. The reset waits for dialog:after-hide because the closing animation runs while the
    // drawer is still displayed.
    drawer.style.display = 'block';
    drawer.addEventListener('dialog:after-hide', () => {
      drawer.style.display = 'none';
    }, { once: true });
    drawer.show();
  };

  // Capture, not bubble: cart-drawer listens for variant:add on document and opens there. A capture listener on the
  // same node runs before every bubble listener on it, whatever order they registered in.
  document.addEventListener('variant:add', (event) => {
    const form = event.target;

    // getAttribute, never form.id: a form's named-element lookup overrides the IDL attribute, so form.id is the
    // <input name="id"> this very form posts, exactly as theme.js relies on in ProductForm.
    if (!(form instanceof HTMLFormElement)) return;

    const formId = form.getAttribute('id') || '';
    if (!formId.startsWith(FORM_ID_PREFIX)) return;

    const drawer = document.getElementById(`${formId}${DRAWER_ID_SUFFIX}`);
    const markup = event.detail?.cart?.sections?.['variant-added'];
    const section = markup
      ? new DOMParser().parseFromString(markup, 'text/html').querySelector('.shopify-section')
      : null;

    // With nothing to show, leave the event untouched so the theme's cart drawer still confirms the add.
    if (!drawer || !section || typeof drawer.show !== 'function') return;

    event.detail.blockCartDrawerOpening = true;
    showConfirmation(drawer, section);
  }, true);
})();

/* --- Cart quantity stepper (BL) — show every click, send one request ------------------------------------------------
   Wraps a cart line's quantity field with plus and minus buttons when Theme settings > Cart > "Show quantity plus and
   minus buttons" is on. The theme's own quantity-selector (theme.js:2566) cannot be used here: it dispatches change on
   every click, and in the cart each change is a POST to /cart/change.js, so a burst of clicks raced a burst of
   requests while cart-drawer rebuilt its markup underneath them (theme.js:1849 does that 1250ms after a change) and
   the line settled one step away from where the shopper stopped.

   The number moves on every click, and the change event that actually updates the cart is dispatched once the clicking
   stops. It is dispatched from the input, so line-item-quantity (theme.js:2059) reads the same target, the same
   data-line-key and the same value it always did — this element decides WHEN the cart is told, never what it is told.

   Once a change is on its way, the WHOLE CART is closed for business until it settles. A line is addressed by the key
   the page rendered, and the page only gets fresh keys when it re-renders: theme.js:2107 reloads the cart page,
   cart-drawer rebuilds itself 1250ms later. Anything dispatched inside that window still carries keys from before the
   change, which may already have been replaced server-side, and the cart answers "no valid id or line parameter".

   Cart-wide rather than per line, because the settling is cart-wide: the cart page RELOADS on success, so a change
   started on another line while the first was in flight is thrown away by the navigation, and the drawer's rebuild
   replaces every line's markup at once. A shopper who edits a second line during that window loses the edit either
   way, so the honest thing is to not accept it. Remove locks with the steppers — it posts the same kind of key to the
   same endpoint — and a line whose control is withheld entirely, as a free gift's is, still has its Remove locked.
------------------------------------------------------------------------------------------------------------------- */
(() => {
  // Long enough to swallow a burst of impatient clicks, short enough that a single click still feels immediate.
  const COMMIT_DELAY = 450;
  const INPUT_SELECTOR = '.quantity-input';
  // A lock is lifted by its change settling. This releases one that never did — a dropped connection, a response the
  // theme discarded — so a line can never be left permanently unusable.
  const LOCK_TIMEOUT = 10000;

  // The release timer is the lock: a timer means a change is in flight. The attributes below are only how it looks.
  let releaseTimer = null;

  const isLocked = () => releaseTimer !== null;

  const paint = (busy) => {
    // Every group, not only those holding a field: a line whose quantity control is withheld still has a Remove link,
    // and that writes to the cart too.
    document.querySelectorAll('line-item-quantity, bl-quantity-stepper')
      .forEach((element) => element.toggleAttribute('data-busy', busy));

    // Read-only rather than disabled: a disabled field is skipped by the theme's own markup reads, and the quantity
    // still has to be legible while it saves.
    document.querySelectorAll(`${INPUT_SELECTOR}[data-line-key]`)
      .forEach((field) => field.toggleAttribute('readonly', busy));
  };

  const lock = () => {
    clearTimeout(releaseTimer);
    releaseTimer = setTimeout(release, LOCK_TIMEOUT);
    paint(true);
  };

  const release = () => {
    if (!isLocked()) return;
    clearTimeout(releaseTimer);
    releaseTimer = null;
    paint(false);
  };

  // Every route a second write could take while the first is in flight, blocked on the way down: a change from any
  // line's field, and any click inside a locked group, which is what stops Remove.
  document.addEventListener('change', (event) => {
    if (isLocked() && event.target?.dataset?.lineKey) event.stopPropagation();
  }, true);

  document.addEventListener('click', (event) => {
    if (!event.target.closest?.('line-item-quantity[data-busy], bl-quantity-stepper[data-busy]')) return;
    event.preventDefault();
    event.stopPropagation();
  }, true);

  // Rejected: the theme has already put the field back to the quantity the cart still holds, so hand the cart back.
  document.addEventListener('line-item:error', release);

  // Accepted: the drawer re-renders rather than reloading, so the lock has to be lifted for the markup that replaces
  // this one. cart:change is the catch-all — the cart has moved, so nothing still in flight can be trusted anyway.
  document.addEventListener('line-item:change', release);
  document.documentElement.addEventListener('cart:change', release);

  class BlQuantityStepper extends HTMLElement {
    #abortController;
    #timer = null;

    connectedCallback() {
      this.#abortController?.abort();
      this.#abortController = new AbortController();
      this.addEventListener('click', this.#onClick, { signal: this.#abortController.signal });
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#handOverPendingStep();
    }

    get #input() {
      return this.querySelector(INPUT_SELECTOR);
    }

    #onClick = (event) => {
      const button = event.target.closest('[data-step]');
      const input = this.#input;
      if (!button || !this.contains(button) || !input) return;

      // The CSS makes a locked control inert; this is the half that does not depend on a stylesheet having loaded.
      if (isLocked()) return;

      const before = input.value;
      // The native steppers, as the theme's own selector uses: they honour this line's min, max and step increment.
      if (button.dataset.step === 'up') {
        input.stepUp();
      } else {
        input.stepDown();
      }

      // Already at the line's limit, so there is nothing to show and nothing to tell the cart.
      if (input.value === before) return;

      // theme.js sets this from its own input and change handlers; a button step never passes through either.
      input.style.setProperty('--quantity-input-characters-count', `${input.value.length}ch`);

      clearTimeout(this.#timer);
      this.#timer = setTimeout(this.#commit, COMMIT_DELAY);
    };

    #commit = () => {
      this.#timer = null;
      const input = this.#input;

      // defaultValue is the quantity Liquid rendered, which is what the cart already holds. A burst that returns to it
      // — one up, one down — has nothing to say, and saying it would cost a request and a drawer rebuild.
      if (!input || input.value === input.defaultValue) return;

      input.dispatchEvent(new Event('change', { bubbles: true }));
      // Locked after the dispatch, never before: the guard above drops every change raised while locked, and this one
      // is the change being followed.
      lock();
    };

    #handOverPendingStep = () => {
      if (this.#timer === null) return;

      clearTimeout(this.#timer);
      this.#timer = null;

      const input = this.#input;
      if (!input || input.value === input.defaultValue) return;

      // The cart drawer replaces its whole inner markup after a change, detaching this element with a step still
      // pending. The freshly rendered field for the same line takes it over, so a click during that rebuild is not
      // silently dropped. Custom element reactions run once the innerHTML assignment has finished, so the replacement
      // is already in the document by the time this runs.
      const replacement = document.querySelector(`${INPUT_SELECTOR}[data-line-key="${CSS.escape(input.dataset.lineKey || '')}"]`);
      if (!replacement || replacement === input || replacement.value === input.value) return;

      replacement.value = input.value;
      replacement.dispatchEvent(new Event('change', { bubbles: true }));
    };
  }

  if (!window.customElements.get('bl-quantity-stepper')) {
    window.customElements.define('bl-quantity-stepper', BlQuantityStepper);
  }
})();

/* --- Cart page line item loader (BL) — reach the loader the table layout hides ---------------------------------------
   line-item-quantity finds the element it reports progress to with this.closest("line-item") (theme.js:2081). In the
   cart drawer the quantity sits inside its line item, so that works and the pill loader over the product image turns
   on. On the cart page the quantity is a separate table cell, a sibling of the cell holding the line item, so closest
   returns null, every dispatch is optional-chained away, and a shopper changing a quantity gets no feedback at all
   until the page reloads.

   This supplies the two dispatches the layout swallowed. The theme's own LineItem element (theme.js:1920) still owns
   the loader; nothing here touches aria-busy directly.
------------------------------------------------------------------------------------------------------------------- */
(() => {
  const rowObservers = new WeakMap();

  const stopWatching = (row) => {
    rowObservers.get(row)?.disconnect();
    rowObservers.delete(row);
  };

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLElement) || !input.matches('[data-line-key]')) return;

    // The drawer, and this page's own mobile line, already reach their line item on their own.
    if (input.closest('line-item')) return;

    const row = input.closest('tr');
    const lineItem = row?.querySelector('line-item');

    // theme.js:1928 sets aria-busy on the loader without a null check, and line-item.liquid only renders one beside a
    // product image. Dispatching for an imageless line would throw inside the theme rather than show anything.
    if (!lineItem?.querySelector('pill-loader')) return;

    stopWatching(row);
    lineItem.dispatchEvent(new CustomEvent('line-item:will-change', {
      bubbles: true,
      detail: { targetQuantity: parseInt(input.value) }
    }));

    // A successful change reloads the cart page (theme.js:2107), which clears the loader with everything else. A
    // rejected one only inserts its message into the row, so that insertion is what has to switch the loader off.
    const observer = new MutationObserver((records) => {
      const errored = records.some((record) => Array.from(record.addedNodes).some(
        (node) => node.nodeType === Node.ELEMENT_NODE && node.matches('[role="alert"]')
      ));
      if (!errored) return;

      stopWatching(row);
      lineItem.dispatchEvent(new CustomEvent('line-item:error', { bubbles: true }));
    });

    observer.observe(row, { childList: true, subtree: true });
    rowObservers.set(row, observer);
  });
})();

/* --- Cart page staleness recovery (BL) — never leave a row that cannot be changed ------------------------------------
   A cart line is addressed by its key ("variantId:hash"), and the cart page renders those keys once. It re-renders only
   after a SUCCESSFUL change (theme.js:2107 reloads), so once a key stops matching the live cart — the shopper changed
   the cart in another tab, came back through the browser's back button, or an app or Shopify Function rewrote the line
   — every click posts a key the cart no longer has, /cart/change.js answers "no valid id or line parameter", and the
   page stays wrong until the shopper reloads by hand.

   Two halves: come back from the back button with a fresh page, and if a change is rejected because the line is gone,
   reload instead of leaving a dead row.
------------------------------------------------------------------------------------------------------------------- */
(() => {
  const onCartPage = () => window.themeVariables?.settings?.pageType === 'cart';

  // cart-drawer already refreshes itself on a back/forward restore (theme.js:1864). The cart page never got the same
  // treatment, so it is restored with whatever keys it rendered before the shopper left.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted && onCartPage()) window.location.reload();
  });

  document.addEventListener('line-item:error', async (event) => {
    if (!onCartPage()) return;

    const row = event.target.closest('tr');
    const key = row?.querySelector('[data-line-key]')?.dataset.lineKey;
    if (!key) return;

    let cart;
    try {
      cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
    } catch (error) {
      return;
    }

    // The line is still there, so the rejection was about the request itself — a stock limit or a quantity rule. That
    // message is the shopper's answer and must stay on screen. Deciding on whether the key resolves rather than on the
    // message text also keeps this working in every language the storefront is served in.
    if (cart.items?.some((item) => item.key === key)) return;

    window.location.reload();
  });
})();
